import type { ApiModule } from './ApiModule';
import type { LogLevel } from 'effect';

import { Context, Layer } from 'effect';

/**
 * Configuration for API operation logging.
 *
 * `moduleOverrides` maps an `ApiModule` to a `LogLevel.LogLevel`:
 *
 * - A `Severity` (`Fatal` / `Error` / `Warn` / `Info` / `Debug` / `Trace`)
 *   replaces the call-site severity for matching modules.
 * - `'All'` elevates the module's calls to `Fatal` so they pass any
 *   minimum-level filter (the v4 equivalent of "always log").
 * - `'None'` silences the module — matching calls emit nothing.
 *
 * For a global minimum threshold (e.g. silence everything below `Warn`),
 * use Effect's `References.MinimumLogLevel` at the layer level.
 */
export type LoggerConfigShape = {
  readonly moduleOverrides: ReadonlyMap<ApiModule, LogLevel.LogLevel>;
};

export class LoggerConfig extends Context.Service<LoggerConfig, LoggerConfigShape>()(
  'LoggerConfig'
) {}

export const LoggerConfigLive = Layer.succeed(LoggerConfig, {
  moduleOverrides: new Map<ApiModule, LogLevel.LogLevel>([
    // Example: Silence noisy product list logs
    // [ApiModule.ProductList, 'None'],
    // Example: Always log order operations at top severity
    // [ApiModule.OrderCreate, 'All'],
    // Example: Force info level for brand list
    // [ApiModule.BrandList, 'Info'],
  ]),
});
